import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";

/**
 * Hidden administrator sign-in.
 *
 *   Route: /admin-login  (also mapped from /admin for convenience)
 *
 * NOT linked from the public site. Requires role === "admin" — student-role
 * accounts using this URL will be signed out immediately with an error.
 */
export default function AdminLogin() {
  const { login, logout } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setErr(""); setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (!r.ok) return setErr(r.error);
    if (r.user.role !== "admin") {
      await logout();
      setErr("This portal is for administrators only.");
      return;
    }
    toast.success(`Welcome back, ${r.user.name}!`);
    nav("/app/admin");
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B1B3A] px-6 py-12 relative overflow-hidden grain" data-testid="admin-login-page">
      <div className="absolute -top-20 -right-16 h-80 w-80 rounded-full bg-teal/20 blur-3xl" />
      <div className="absolute -bottom-24 -left-16 h-96 w-96 rounded-full bg-magenta/20 blur-3xl" />
      <div className="relative w-full max-w-md bg-[#0F2350]/80 border border-white/10 rounded-3xl p-8 backdrop-blur-xl shadow-2xl">
        <div className="flex items-center justify-between">
          <Logo size={40} onLight={false} linkTo={null} />
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/15 text-white/80 text-[10px] font-semibold uppercase tracking-[0.2em] px-2.5 py-1">
            <ShieldCheck className="h-3 w-3" /> Admin
          </span>
        </div>

        <h1 className="mt-8 font-display text-3xl font-bold text-white tracking-tight">Administrator sign-in</h1>
        <p className="text-white/70 text-sm mt-2">Restricted access. Authorised personnel only.</p>

        <form onSubmit={submit} className="mt-7 space-y-4" data-testid="admin-login-form">
          <div>
            <Label htmlFor="ae" className="text-white/85">Email</Label>
            <Input id="ae" type="email" required autoComplete="username"
              data-testid="admin-login-email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 rounded-xl h-11 bg-white/5 border-white/10 text-white placeholder:text-white/40" />
          </div>
          <div>
            <Label htmlFor="ap" className="text-white/85">Password</Label>
            <div className="relative mt-1.5">
              <Input id="ap" type={show ? "text" : "password"} required autoComplete="current-password"
                data-testid="admin-login-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="rounded-xl h-11 pr-10 bg-white/5 border-white/10 text-white placeholder:text-white/40" />
              <button type="button" onClick={() => setShow(!show)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white">
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          {err && <p data-testid="admin-login-error" className="text-sm text-rose-300">{err}</p>}
          <Button type="submit" data-testid="admin-login-submit" disabled={loading}
            className="w-full h-11 rounded-xl bg-gold text-[#0B1B3A] hover:brightness-95 font-semibold border-0 btn-lift">
            {loading ? "Signing in…" : "Sign in to Admin"}
          </Button>
        </form>

        <p className="mt-6 text-[11px] text-white/50 text-center">
          Not an administrator? Return to the <a href="/" className="text-white/80 hover:underline">learning portal</a>.
        </p>
      </div>
    </div>
  );
}
